// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Facturation — abonnement côté client (onglet Abonnement, parcours Stripe),
// et rentabilité côté admin (coûts réels, tarifs Mistral, marge).
//
// Les deux cohabitent volontairement : ce sont les deux faces du même sujet
// (ce que le client paie / ce que ça nous coûte), et les libellés de forfait y
// sont partagés.
//
// ⚠️ `plus_*` n'est PAS de la facturation malgré l'apparence : c'est le « plus »
// français (« Plus d'options », « Associer un projet »), pas « PRO+ ». Ces clés
// vivent dans le domaine analyse.

const facturation = {
  fr: {
    // ── Rentabilité (admin) ──
    billing_section_title:         '💰 Rentabilité',
    billing_period_label:          'Période',
    billing_scope_total:           'Tous',
    billing_licence_pool_analyses: 'Pool analyses',
    billing_licence_pool_ocr:      'Pool OCR',
    // Cabinet : l'abonnement est sur la ligne du titulaire, le résumé ne porte que le dépassement.
    billing_licence_hors_forfait:  'Hors-forfait facturé',
    billing_hf_nb:                 '{n} analyse(s)',
    billing_hf_cout:               'Coût du hors-forfait',
    billing_hf_marge:              'Marge sur le hors-forfait',
    billing_hf_echec:              '⚠️ {n} analyse(s) hors forfait pas encore transmise(s) à Stripe — elles seront renvoyées automatiquement.',
    billing_pool_title:            'Pool partagé',
    billing_pool_explication:      'Analyses communes à tous les sièges : consommation de la période, coût réel du mois, et ce qui dépasse le pool.',
    billing_pool_explication_cabinet: 'Le prix fixe est dans le tableau ci-dessus. Ici, seulement le dépassement du pool : les analyses hors forfait transmises à Stripe sur la période, leur coût et la marge qu\'elles dégagent.',
    billing_licence_cost_real:     'Coût réel (mois)',
    billing_col_tier:              'Forfait',
    billing_col_analyses:          'Analyses',
    billing_col_cost_avg:          'Coût moyen / analyse',
    // Dit sur quoi le coût moyen est divisé : les analyses qui ont CONSOMMÉ,
    // échecs compris — pas les seules analyses facturées.
    billing_cost_avg_basis:        'Sur {n} analyse(s) ayant consommé',
    billing_col_cost_real:         'Coût total',
    billing_col_billed:            'Facturé',
    billing_col_encaisse:          'Encaissé',
    billing_col_encaisse_infobulle:'Factures réellement payées, HT. Diffère du « Facturé » quand un avoir a été émis — un changement de forfait en cours de mois, par exemple : le crédit sera absorbé par la facture suivante.',
    billing_col_margin:            'Marge',
    billing_vue_mois:              'Le mois',
    billing_vue_glissant:          '12 mois glissants',
    billing_vue_arr:               'Projection annuelle',
    billing_projection_avertissement: 'Projection : le mois choisi multiplié par douze. Ce ne sont pas des montants constatés — un mois atypique est amplifié d’autant.',
    billing_reel_infobulle:        'Montant réellement encaissé chez Stripe',
    billing_theorique_suffixe:     'théorique',
    billing_theorique_infobulle:   'Estimation d’après la grille tarifaire : aucune facture Stripe payée sur la période.',
    billing_theorique_note:        '* estimation d’après la grille tarifaire, faute de facture Stripe sur la période.',
    billing_remise_label:          'Remise',
    billing_no_tier:               'Aucun forfait',
    billing_deleted_account:       'Compte supprimé',
    billing_empty:                 'Aucune donnée de consommation pour ce mois.',
    billing_total_label:           'TOTAL',

    // ── Tarifs Mistral (coût interne) ──
    mistral_pricing_title:         'Tarifs Mistral (coût interne) :',
    mistral_pricing_input:         'Input',
    mistral_pricing_output:        'Output',
    mistral_pricing_ocr:           'OCR',
    mistral_pricing_rate:          'Taux USD/EUR',
    mistral_pricing_refresh_rate:  'Actualiser le taux',
    mistral_pricing_edit:          'Modifier',
    mistral_pricing_save:          'Enregistrer',

    // ── Montée en gamme ──
    // ⚠️ Ne plus parler d'« OCR avancé réservé » : depuis la grille du
    // 05/09/2026, l'OCR est compris dans les TROIS forfaits. Seul le mode
    // Projets distingue encore Essentiel de Pro et Cabinet.
    upgrade_modal_title:           'Passer au forfait Pro',
    upgrade_modal_body:            "Le mode Projets, qui met en relation plusieurs documents d'un même chantier, est compris dans les forfaits Pro et Cabinet.",
    upgrade_modal_cta:             'Voir nos offres',

    // ── Onglet Abonnement (client) ──
    billing_title:                 'Mon abonnement',
    billing_current_plan:          'Plan actuel',
    billing_no_plan:               'Aucun abonnement actif',
    billing_onboarding_title:      'Choisissez votre abonnement',
    billing_onboarding_body:       'Améliorez votre offre pour analyser davantage de rapports et débloquer toutes les fonctionnalités de Normascan.',
    billing_status_active:         'Actif',
    billing_status_past_due:       'Paiement en attente',
    // ⚠️ Le client GARDE son forfait pendant les relances, mais ses analyses
    // sont SUSPENDUES (décision de Marc, 22/09/2026) : le message doit le dire
    // et donner le geste qui débloque.
    billing_past_due_hint:         "Votre dernier paiement n'a pas abouti : les analyses sont suspendues. Votre forfait est conservé — régularisez depuis « Gérer mon abonnement » et elles reprennent aussitôt.",
    billing_status_canceled:       'Résilié',
    billing_status_none:           'Aucun',
    billing_renews_on:             'Renouvellement le',
    billing_ends_on:               'Expire le',
    billing_manage_cta:            'Gérer mon abonnement',
    billing_manage_hint:           'Modifier votre CB, consulter vos factures ou résilier depuis le portail sécurisé Stripe.',
    billing_subscribe_cta:         'Souscrire',
    billing_upgrade_cta:           'Passer à ce plan',
    billing_current_badge:         'Mon forfait actuel',
    // Rétrogradation programmée : DEUX cartes sont concernées, celle qui
    // se termine et celle qui prend le relais. Vocabulaire du marché.
    billing_badge_ending:          'Se termine bientôt',
    billing_badge_starting:        'Prochain forfait',
    billing_starts_on:             'Commencera le',
    // ⚠️ « Conserver ce forfait » et NON « Annuler » : ce dernier se lit
    // « résilier mon abonnement », soit l'inverse de ce que fait le bouton.
    billing_keep_plan_cta:         'Conserver ce forfait',
    billing_plan_kept:             'Changement annulé. Vous conservez votre forfait actuel.',
    // ⚠️ Le palier n'a PAS changé tant que le paiement n'est pas confirmé.
    billing_payment_required:      "Votre banque demande une confirmation pour ce paiement. Votre forfait n'a pas été modifié : réessayez, ou vérifiez votre carte depuis « Gérer mon abonnement ».",
    // ⚠️ La page Stripe s'ouvre dans un NOUVEL onglet : elle n'a pas de
    // bouton retour, et remplacer Normascan piégeait qui renonçait.
    billing_payment_opened:        "La page de paiement s'est ouverte dans un nouvel onglet. Votre forfait changera une fois le paiement confirmé — vous pouvez aussi abandonner ce changement ci-dessous.",
    billing_pending_title:         'Changement en attente de paiement :',
    billing_pending_body:          "Votre banque n'a pas confirmé ce paiement. Votre forfait actuel reste inchangé tant que ce n'est pas réglé.",
    billing_pending_pay_cta:       'Finaliser le paiement',
    billing_pending_abandon_cta:   'Abandonner ce changement',
    billing_pending_abandoned:     'Changement abandonné. Vous conservez votre forfait actuel, rien ne sera prélevé.',
    // ⚠️ « prend fin le », et surtout pas « annulé » : le forfait fonctionne
    // normalement jusqu'à cette date, c'est la période déjà payée. Écrire
    // « annulé » ferait croire à une coupure immédiate, et inquiéterait un
    // client qui a encore ses analyses.
    billing_canceling_title:       'Votre abonnement prend fin le',
    billing_canceling_body:        "Vous conservez l'intégralité de votre forfait jusqu'à cette date. Passé ce terme, votre compte repassera en accès gratuit et aucun nouveau prélèvement n'aura lieu.",
    billing_resume_cta:            'Reprendre mon abonnement',
    billing_resumed:               "Résiliation annulée. Votre abonnement se poursuit normalement — rien ne vous a été prélevé.",
    billing_confirm_resume_warning:"Attention : votre abonnement était résilié. Valider ce changement annule cette résiliation et le reconduit aux conditions du nouveau forfait.",
    // ⚠️ Le singulier et le pluriel sont deux clés, sans interpolation : le
    // nombre est rendu à part, en gras. Une seule clé avec « collègue(s) »
    // s'afficherait telle quelle dans un message qui annonce déjà une
    // mauvaise nouvelle.
    billing_seats_lost_singular:   "collègue occupe actuellement un siège de votre licence Cabinet et perdra l'accès à Normascan à la fin de la période en cours. Ses analyses restent conservées.",
    billing_seats_lost_plural:     "collègues occupent actuellement un siège de votre licence Cabinet et perdront l'accès à Normascan à la fin de la période en cours. Leurs analyses restent conservées.",
    // ⛔ Clés annuelles retirées le 08/09/2026 (toggle, remise, équivalent,
    // « / an ») : les forfaits sont mensuels sans engagement. La licence
    // Entreprise reste annuelle mais ne se souscrit pas dans cet écran.
    billing_per_month:             '/ mois',
    billing_excl_tax:              'HT',
    billing_incl_tax:              'TTC',
    billing_detail_ht:             'Montant HT',
    billing_detail_tva:            'TVA',
    billing_detail_total:          'Total prélevé TTC',
    billing_detail_prorata_nouveau:'Nouveau forfait, au prorata des jours restants',
    billing_detail_prorata_credit: 'Forfait actuel déjà payé, non utilisé',
    billing_confirm_upgrade_avoir: 'Le passage à ce forfait est immédiat. Rien ne sera prélevé maintenant : le temps déjà payé sur votre forfait actuel dépasse le montant dû, et un avoir de {montant} € sera déduit de votre prochaine facture.',
    billing_loading:               'Chargement...',
    billing_redirecting:           'Redirection vers Stripe...',
    billing_error:                 'Erreur lors de la redirection. Veuillez réessayer.',
    billing_stripe_success:        'Abonnement activé. Votre compte est à jour.',
    // ⚠️ Une MONTÉE est prélevée aussitôt sur la carte enregistrée ; une BAISSE
    // prend effet à l'échéance (décision du 09/09/2026). Les deux messages
    // doivent le dire sans ambiguïté : c'est de l'argent.
    billing_plan_changed:          "Forfait modifié. La différence vient d'être prélevée sur votre carte.",
    billing_plan_scheduled:        'Changement enregistré. Votre nouveau forfait prendra effet à la fin de la période en cours.',
    billing_plan_unchanged:        'Vous êtes déjà sur ce forfait.',
    billing_confirm_upgrade:       'Le passage à ce forfait est immédiat. Un montant de',
    billing_confirm_upgrade_suite: 'sera prélevé maintenant sur votre carte, au prorata des jours restants. Votre prochaine facture reprendra ensuite le tarif mensuel habituel.',
    billing_confirm_downgrade:     "Rien ne sera prélevé maintenant. Vous conservez votre forfait actuel, déjà payé, jusqu'au",
    billing_confirm_downgrade_suite: ', puis la bascule se fera automatiquement vers le forfait',
    billing_stripe_cancel:         'Paiement annulé. Vous pouvez réessayer à tout moment.',
    // Avantages des cartes de forfait (24/09/2026) : chaque palier liste ce qu'il
    // AJOUTE au précédent, sous « Tout le forfait X, plus : ». ⚠️ Aucun nombre
    // d'analyses ici : il vient de `pricing_tiers` (ligne au-dessus de la liste).
    // ⚠️ Chaque ligne doit être VRAIE dans le code : Projets = `projet_disponible`,
    // certification = `PALIERS_VERIFICATION_CERTIFICATION` (core/droits.py).
    billing_tout_plus:             'Tout le forfait {palier}, plus :',
    billing_feat_ocr:              'OCR des rapports scannés',
    billing_feat_rapport:          "Rapport d'analyse PDF + export Excel",
    billing_feat_stats:            'Statistiques de vos analyses',
    billing_feat_support:          'Support par e-mail',
    billing_feat_projets:          "Mode Projets : les documents d'un même chantier confrontés entre eux",
    billing_feat_certification:    'Vérification des certifications des diagnostiqueurs',
    billing_feat_sieges:           '{n} sièges inclus',
    billing_feat_pool:             'Analyses partagées entre les sièges',
    billing_feat_gestion:          'Gestion des comptes et des droits',
    billing_feat_vue_equipe:       "Vue d'équipe sur les analyses, en lecture seule",
    // `t()` ne sait pas interpoler (cf i18n.js) : le nombre est composé en JSX
    // et cette clé n'en porte que le suffixe.
    billing_analyses_incluses:     'analyses par mois incluses',
    billing_analyse_sup:           'Analyse supplémentaire',
    billing_not_configured:        'Les paiements en ligne ne sont pas encore configurés. Contactez-nous pour souscrire.',

    // ── Identité de facturation avant paiement (chantier SaaS 1, 23/09/2026) ──
    identite_titre:                'Vos informations de facturation',
    identite_intro:                'Normascan est réservé aux professionnels établis en France. Ces informations figureront sur vos factures.',
    identite_profil_label:         'Vous êtes',
    identite_profil_pro:           'Professionnel',
    identite_profil_pro_texte:     'Société, entreprise individuelle ou micro-entreprise',
    identite_siren:                'SIREN',
    identite_siren_aide:           '9 chiffres. Nous remplissons le reste pour vous à partir du répertoire officiel des entreprises.',
    identite_registre_recherche:   'Recherche dans le répertoire officiel des entreprises…',
    identite_registre_trouve:      '✓ Entreprise trouvée : vérifiez les informations ci-dessous.',
    identite_registre_introuvable: 'Ce SIREN n\'apparaît pas dans le répertoire public. C\'est normal si votre entreprise a choisi de ne pas diffuser ses informations : complétez simplement les champs ci-dessous.',
    identite_registre_cesse:       'Selon le répertoire officiel, cette entreprise a cessé son activité. Vérifiez votre SIREN.',
    identite_raison_sociale:       'Raison sociale (ou nom de l\'entrepreneur)',
    identite_adresse:              'Adresse',
    identite_code_postal:          'Code postal',
    identite_ville:                'Ville',
    identite_tva:                  'N° de TVA intracommunautaire',
    identite_email_facturation:    'E-mail pour recevoir les factures',
    identite_pays:                 'Pays',
    identite_pays_france:          'France',
    identite_facultatif:           'facultatif',
    identite_pourquoi:             'Pourquoi le SIREN ? À partir du 1er septembre 2027, les factures entre professionnels deviennent électroniques et doivent l\'indiquer.',
    identite_annuler:              'Annuler',
    identite_envoi:                'Enregistrement…',
    identite_continuer_paiement:   'Continuer vers le paiement',
    identite_enregistrer:          'Enregistrer',
    identite_enregistree:          'Vos informations de facturation sont enregistrées.',
    identite_bandeau_titre:        'Complétez vos informations de facturation',
    identite_bandeau_texte:        'Votre SIREN nous sera indispensable pour vous facturer à partir du 1er septembre 2027. Cela prend une minute.',
    identite_bandeau_bouton:       'Compléter',
  },

  en: {
    // ── Profitability (admin) ──
    billing_section_title:         '💰 Profitability',
    billing_period_label:          'Period',
    billing_scope_total:           'All',
    billing_licence_pool_analyses: 'Analysis pool',
    billing_licence_pool_ocr:      'OCR pool',
    billing_licence_hors_forfait:  'Overage billed',
    billing_hf_nb:                 '{n} analysis(es)',
    billing_hf_cout:               'Overage cost',
    billing_hf_marge:              'Overage margin',
    billing_hf_echec:              '⚠️ {n} overage analysis(es) not yet sent to Stripe — they will be resent automatically.',
    billing_pool_title:            'Shared pool',
    billing_pool_explication:      'Analyses shared by all seats: usage over the period, real cost of the month, and what exceeds the pool.',
    billing_pool_explication_cabinet: 'The fixed price is in the table above. Here, only the pool overage: overage analyses sent to Stripe over the period, their cost and the margin they bring.',
    billing_licence_cost_real:     'Real cost (month)',
    billing_col_tier:              'Plan',
    billing_col_analyses:          'Analyses',
    billing_col_cost_avg:          'Avg. cost / analysis',
    billing_cost_avg_basis:        'Over {n} analysis(es) that consumed',
    billing_col_cost_real:         'Total cost',
    billing_col_billed:            'Billed',
    billing_col_encaisse:          'Collected',
    billing_col_encaisse_infobulle:'Invoices actually paid, excl. tax. Differs from "Billed" when a credit note was issued — a mid-month plan change, for instance: the credit will be absorbed by the next invoice.',
    billing_col_margin:            'Margin',
    billing_vue_mois:              'Single month',
    billing_vue_glissant:          'Trailing 12 months',
    billing_vue_arr:               'Annual projection',
    billing_projection_avertissement: 'Projection: the selected month multiplied by twelve. These are not recorded amounts — an unusual month is amplified accordingly.',
    billing_reel_infobulle:        'Amount actually collected through Stripe',
    billing_theorique_suffixe:     'estimated',
    billing_theorique_infobulle:   'Estimated from the price list: no paid Stripe invoice for this period.',
    billing_theorique_note:        '* estimated from the price list, no Stripe invoice for this period.',
    billing_remise_label:          'Discount',
    billing_no_tier:               'No plan',
    billing_deleted_account:       'Deleted account',
    billing_empty:                 'No usage data for this month.',
    billing_total_label:           'TOTAL',

    // ── Mistral rates (internal cost) ──
    mistral_pricing_title:         'Mistral rates (internal cost):',
    mistral_pricing_input:         'Input',
    mistral_pricing_output:        'Output',
    mistral_pricing_ocr:           'OCR',
    mistral_pricing_rate:          'USD/EUR rate',
    mistral_pricing_refresh_rate:  'Refresh rate',
    mistral_pricing_edit:          'Edit',
    mistral_pricing_save:          'Save',

    // ── Upgrade ──
    upgrade_modal_title:           'Upgrade to Pro',
    upgrade_modal_body:            'Project mode, which links several documents from the same site together, is included in the Pro and Cabinet plans.',
    upgrade_modal_cta:             'See our offers',

    // ── Subscription tab (client) ──
    billing_title:                 'My subscription',
    billing_current_plan:          'Current plan',
    billing_no_plan:               'No active subscription',
    billing_onboarding_title:      'Choose your plan',
    billing_onboarding_body:       'Upgrade your plan to analyse more reports and unlock all Normascan features.',
    billing_status_active:         'Active',
    billing_status_past_due:       'Payment pending',
    billing_past_due_hint:         'Your last payment did not go through: analyses are suspended. Your plan is kept — settle it from "Manage subscription" and they resume immediately.',
    billing_status_canceled:       'Cancelled',
    billing_status_none:           'None',
    billing_renews_on:             'Renews on',
    billing_ends_on:               'Expires on',
    billing_manage_cta:            'Manage subscription',
    billing_manage_hint:           'Update your card, view invoices or cancel from the secure Stripe portal.',
    billing_subscribe_cta:         'Subscribe',
    billing_upgrade_cta:           'Switch to this plan',
    billing_current_badge:         'My current plan',
    billing_badge_ending:          'Ending soon',
    billing_badge_starting:        'Next plan',
    billing_starts_on:             'Starts on',
    billing_keep_plan_cta:         'Keep this plan',
    billing_plan_kept:             'Change cancelled. You keep your current plan.',
    billing_payment_required:      'Your bank requires confirmation for this payment. Your plan has not been changed: try again, or check your card from "Manage subscription".',
    billing_payment_opened:        'The payment page opened in a new tab. Your plan will change once the payment is confirmed — you can also abandon this change below.',
    billing_pending_title:         'Change awaiting payment:',
    billing_pending_body:          'Your bank has not confirmed this payment. Your current plan stays unchanged until it is settled.',
    billing_pending_pay_cta:       'Complete payment',
    billing_pending_abandon_cta:   'Abandon this change',
    billing_pending_abandoned:     'Change abandoned. You keep your current plan, nothing will be charged.',
    billing_canceling_title:       'Your subscription ends on',
    billing_canceling_body:        'You keep your full plan until that date. After it, your account returns to free access and no further charge will be made.',
    billing_resume_cta:            'Resume my subscription',
    billing_resumed:               'Cancellation reversed. Your subscription continues as normal — nothing has been charged.',
    billing_confirm_resume_warning:'Note: your subscription was cancelled. Confirming this change reverses that cancellation and renews it under the new plan.',
    billing_seats_lost_singular:   'colleague currently holds a seat on your Cabinet licence and will lose access to Normascan at the end of the current period. Their analyses are kept.',
    billing_seats_lost_plural:     'colleagues currently hold a seat on your Cabinet licence and will lose access to Normascan at the end of the current period. Their analyses are kept.',
    billing_per_month:             '/ month',
    billing_excl_tax:              'excl. tax',
    billing_incl_tax:              'incl. tax',
    billing_detail_ht:             'Amount excl. tax',
    billing_detail_tva:            'VAT',
    billing_detail_total:          'Total charged incl. tax',
    billing_detail_prorata_nouveau:'New plan, prorated for the remaining days',
    billing_detail_prorata_credit: 'Current plan already paid, unused',
    billing_confirm_upgrade_avoir: 'The change takes effect immediately. Nothing will be charged now: the time already paid on your current plan exceeds the amount due, and a credit of {montant} € will be deducted from your next invoice.',
    billing_loading:               'Loading...',
    billing_redirecting:           'Redirecting to Stripe...',
    billing_error:                 'Redirect error. Please try again.',
    billing_stripe_success:        'Subscription activated. Your account is up to date.',
    billing_plan_changed:          'Plan changed. The difference has just been charged to your card.',
    billing_plan_scheduled:        'Change saved. Your new plan will take effect at the end of the current period.',
    billing_plan_unchanged:        'You are already on this plan.',
    billing_confirm_upgrade:       'Switching is immediate. An amount of',
    billing_confirm_upgrade_suite: 'will be charged to your card now, prorated for the remaining days. Your next invoice will then show the usual monthly rate.',
    billing_confirm_downgrade:     'Nothing will be charged now. You keep your current plan, already paid for, until',
    billing_confirm_downgrade_suite: ', then it will switch automatically to the plan',
    billing_stripe_cancel:         'Payment cancelled. You can try again at any time.',
    billing_tout_plus:             'Everything in {palier}, plus:',
    billing_feat_ocr:              'OCR for scanned reports',
    billing_feat_rapport:          'PDF analysis report + Excel export',
    billing_feat_stats:            'Statistics on your analyses',
    billing_feat_support:          'Email support',
    billing_feat_projets:          'Project mode: documents from the same site checked against each other',
    billing_feat_certification:    'Diagnostician certification check',
    billing_feat_sieges:           '{n} seats included',
    billing_feat_pool:             'Analyses shared between seats',
    billing_feat_gestion:          'Account and permission management',
    billing_feat_vue_equipe:       'Team view of analyses, read-only',
    billing_analyses_incluses:     'analyses per month included',
    billing_analyse_sup:           'Additional analysis',
    billing_not_configured:        'Online payments are not yet configured. Contact us to subscribe.',

    // ── Billing identity before payment (SaaS project 1, 23/09/2026) ──
    identite_titre:                'Your billing details',
    identite_intro:                'Normascan is reserved for professionals established in France. These details will appear on your invoices.',
    identite_profil_label:         'You are',
    identite_profil_pro:           'Professional',
    identite_profil_pro_texte:     'Company, sole proprietorship or micro-enterprise',
    identite_siren:                'SIREN',
    identite_siren_aide:           '9 digits. We fill in the rest for you from the official business register.',
    identite_registre_recherche:   'Searching the official business register…',
    identite_registre_trouve:      '✓ Company found: please check the details below.',
    identite_registre_introuvable: 'This SIREN is not in the public register. This is normal if your business chose not to publish its details: just fill in the fields below.',
    identite_registre_cesse:       'According to the official register, this business has ceased trading. Please check your SIREN.',
    identite_raison_sociale:       'Company name (or entrepreneur\'s name)',
    identite_adresse:              'Address',
    identite_code_postal:          'Postal code',
    identite_ville:                'City',
    identite_tva:                  'EU VAT number',
    identite_email_facturation:    'E-mail to receive invoices',
    identite_pays:                 'Country',
    identite_pays_france:          'France',
    identite_facultatif:           'optional',
    identite_pourquoi:             'Why the SIREN? From 1 September 2027, invoices between businesses become electronic and must show it.',
    identite_annuler:              'Cancel',
    identite_envoi:                'Saving…',
    identite_continuer_paiement:   'Continue to payment',
    identite_enregistrer:          'Save',
    identite_enregistree:          'Your billing details have been saved.',
    identite_bandeau_titre:        'Complete your billing details',
    identite_bandeau_texte:        'We will need your SIREN to invoice you from 1 September 2027. It takes a minute.',
    identite_bandeau_bouton:       'Complete',
  },
};

export default facturation;
